import { APP_DEFAULTS } from "../../defaults";

// Détection de l'environnement
const isProduction = window.location.hostname.includes("ceven-vtc.fr");
const isAiStudio = !isProduction;
const BRIDGE_URL = "https://ceven-vtc.fr/bridge.php";
const BRIDGE_SECRET = "JE M'APPELLE GERALD VERDIER ET JE SUIS NE LE 09041974";

export const storageService = {
  /**
   * Télécharge une image vers le serveur Ionos
   */
  async uploadImageToIonos(imageUrl: string, siteId: string, siteName: string = "", clean: boolean = false): Promise<string> {
    console.log(`[IONOS] Tentative de transfert d'image pour le site ${siteId} (${siteName})...`);
    
    try {
      const response = await fetch("https://ceven-vtc.fr/api/save_image.php", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: "CevenVTC2024!",
          imageUrl: imageUrl,
          siteId: siteId,
          siteName: siteName,
          clean: clean
        })
      });
      
      const result = await response.json();
      console.log("[IONOS] Réponse brute du serveur:", result);
      
      if (result.localUrl) {
        console.log(`[IONOS] Succès ! Nouvelle URL : ${result.localUrl}`);
        return result.localUrl;
      } else {
        console.error(`[IONOS] Échec du script PHP. Résultat complet:`, result);
        throw new Error(result.error || "Erreur lors du transfert de l'image");
      }
    } catch (e) {
      console.error("[IONOS] Erreur réseau ou serveur lors du transfert :", e);
      throw e;
    }
  },

  /**
   * Cache pour éviter les appels redondants
   */
  _loadPromise: null as Promise<any> | null,

  /**
   * Charge les données depuis la source appropriée
   */
  async loadData(filename?: string, signal?: AbortSignal) {
    // Si un chargement est déjà en cours pour le fichier par défaut, on réutilise la promesse
    if (!filename && this._loadPromise) {
      return this._loadPromise;
    }

    const targetFile = filename || (isAiStudio ? "app_data_preview.json" : "app_data.json");
    
    const fetchPromise = (async () => {
      if (isAiStudio) {
        try {
          const response = await fetch(`/api/data?file=${targetFile}`, { signal });
          if (response.ok) {
            const data = await response.json();
            return data;
          }
        } catch (e: any) {
          if (e.name === 'AbortError' || e.message?.includes('aborted')) throw e;
          // console.warn("Erreur chargement local, tentative Bridge...");
        }
      }

      // Mode Production : On utilise le bridge IONOS directement
      try {
        const response = await fetch(BRIDGE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Bridge-Key': BRIDGE_SECRET
          },
          signal,
          body: JSON.stringify({
            action: 'read',
            file: targetFile,
            secret_key: BRIDGE_SECRET
          })
        });
        const result = await response.json();
        return result.success ? result.data : APP_DEFAULTS;
      } catch (e: any) {
        if (e.name === 'AbortError' || e.message?.includes('aborted')) throw e;
        console.error("Erreur chargement Bridge:", e);
        return APP_DEFAULTS;
      }
    })();

    if (!filename) {
      this._loadPromise = fetchPromise;
      // On nettoie le cache après un court délai ou une fois terminé
      fetchPromise.finally(() => {
        setTimeout(() => {
          if (this._loadPromise === fetchPromise) this._loadPromise = null;
        }, 1000);
      });
    }

    return fetchPromise;
  },

  /**
   * Sauvegarde une image
   */
  async saveImage(fileName: string, dataUrl: string) {
    console.log(`[BRIDGE] Tentative de sauvegarde image via bridge: ${fileName}`);
    try {
      const response = await fetch(BRIDGE_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-Bridge-Key': BRIDGE_SECRET
        },
        body: JSON.stringify({
          action: 'save_image',
          fileName: fileName,
          dataUrl: dataUrl,
          secret_key: BRIDGE_SECRET
        })
      });
      
      const result = await response.json();
      console.log("[BRIDGE] Réponse sauvegarde image:", result);
      
      if (!result.success) throw new Error(result.error || "Erreur lors de la sauvegarde de l'image");
      return result;
    } catch (e) {
      console.error("[BRIDGE] Erreur sauvegarde image:", e);
      throw e;
    }
  },

  /**
   * Upload un fichier physique vers le serveur
   */
  async uploadFile(file: File, folder: string): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    formData.append('key', "CevenVTC2024!");

    console.log(`[IONOS] Tentative d'upload vers ${folder}...`);

    try {
      const response = await fetch("https://ceven-vtc.fr/api/upload.php", {
        method: 'POST',
        mode: 'cors',
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[IONOS] Erreur HTTP ${response.status}:`, errorText);
        throw new Error(`Erreur serveur (${response.status}): ${errorText}`);
      }

      const result = await response.json();
      if (result.url) {
        return result.url; // Retourne l'URL relative (ex: /assets/video/file.mp4)
      } else {
        throw new Error(result.error || "Erreur lors de l'upload du fichier");
      }
    } catch (e) {
      console.error("[IONOS] Erreur upload fichier :", e);
      throw new Error("Impossible de contacter le serveur d'upload. Vérifiez votre connexion et la configuration CORS sur le serveur.");
    }
  },

  /**
   * Nettoie les données pour éviter de saturer le localStorage
   */
  sanitizeAppData(data: any) {
    if (!data) return data;
    const sanitized = { ...data };
    // Liste des champs contenant des données binaires/base64 trop lourdes
    const heavyFields = [
      'heroImageBase64', 
      'pwaVideoUrl', 
      'pwaLogoAccrocheUrl', 
      'pwaLogoBoutonUrl'
    ];
    
    if (sanitized.settings) {
      heavyFields.forEach(field => {
        if (sanitized.settings[field]) {
          delete sanitized.settings[field];
        }
      });
    }
    return sanitized;
  },

  /**
   * Sauvegarde les données
   */
  async saveData(data: any, isPublishing: boolean = false) {
    const targetFile = isAiStudio 
      ? (isPublishing ? "app_data.json" : "app_data_preview.json")
      : "app_data.json"; // En production, on sauvegarde toujours sur app_data.json
    
    if (isAiStudio) {
      const endpoint = isPublishing ? "/api/publish" : "/api/save";
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data)
        });
        if (response.ok) {
          return await response.json();
        }
      } catch (e) {
        console.warn("Local save failed, falling back to Bridge...", e);
      }
    }
    
    // Mode Production (ou fallback AI Studio) : On utilise le bridge IONOS directement
    try {
      const response = await fetch(BRIDGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Bridge-Key': BRIDGE_SECRET
        },
        body: JSON.stringify({
          action: 'write',
          file: targetFile,
          data: data,
          secret_key: BRIDGE_SECRET
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur serveur IONOS (${response.status}): ${errorText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Le Pont IONOS a refusé l'écriture.");
      }
      return result;
    } catch (e) {
      console.error("Erreur sauvegarde Bridge:", e);
      throw e;
    }
  }
};
